import { createElement } from 'react';
import type { inferRouterOutputs } from '@trpc/server';
import type { AppRouter } from '@/lib/trpc/router';
import { ReferenceUnresolvedContent } from './ReferenceUnresolvedContent';
import {
  mapCuratedRecordToSourceContent,
  mapCuratedRecordsToSourceContent,
} from './referenceCuratedRecordMapper';
import type { FacesheetSourceContent } from './types';

type GetPatientFacesheetReferenceDetailResponse =
  inferRouterOutputs<AppRouter>['patientsRouter']['getPatientFacesheetReferenceDetail'];

export type MappedReferenceDetail = {
  source: FacesheetSourceContent;
  targetPage?: number;
  title: string;
  referenceDomain?: string | null;
};

const buildUnresolvedSource = (
  reason?: string,
  message?: string | null,
): FacesheetSourceContent => ({
  kind: 'template',
  render: () =>
    createElement(ReferenceUnresolvedContent, {
      reason,
      message,
    }),
});

export const mapReferenceDetailToViewerContent = (
  detail: GetPatientFacesheetReferenceDetailResponse,
  contextRecords?: GetPatientFacesheetReferenceDetailResponse['contextRecords'],
): MappedReferenceDetail => {
  const scopedContextRecords = contextRecords ?? detail.contextRecords ?? [];
  const isHieCuratedRecord =
    detail.reportScope?.sourceType === 'digitalHealthDataHie' ||
    detail.curatedRecord?.sourceType === 'digitalHealthDataHie';
  if (isHieCuratedRecord && scopedContextRecords.length > 0) {
    return {
      source: mapCuratedRecordsToSourceContent(
        scopedContextRecords,
        detail.title,
        detail.highlightedRecordId,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  if (
    detail.curatedRecord &&
    (detail.curatedRecord.sourceType === 'digitalHealthDataHie' ||
      detail.curatedRecord.sourceType === 'medicationsDigitalData')
  ) {
    return {
      source: mapCuratedRecordToSourceContent(
        detail.curatedRecord,
        detail.title,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  const currentReference = detail.currentReferenceDownload;
  const signedUrl = currentReference?.signedUrl ?? detail.document?.signedUrl;
  if (signedUrl) {
    return {
      source: {
        kind: 'pdf',
        signedUrl,
      },
      targetPage:
        currentReference?.pageNumber ??
        detail.document?.sourcePageNumber ??
        undefined,
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  if (detail.curatedRecord) {
    return {
      source: mapCuratedRecordToSourceContent(
        detail.curatedRecord,
        detail.title,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  if (detail.unresolved) {
    return {
      source: buildUnresolvedSource(
        detail.unresolved.reason,
        detail.unresolved.message,
      ),
      title: detail.title,
      referenceDomain: detail.referenceDomain,
    };
  }

  return {
    source: buildUnresolvedSource(
      'missingContent',
      'No reference content is available for this item.',
    ),
    title: detail.title,
    referenceDomain: detail.referenceDomain,
  };
};
